import { Injectable, NgZone, OnDestroy, inject } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import * as signalR from '@microsoft/signalr';

/**
 * B3 (WS5 #10 Stage B) — native port of the AngularJS `emailBannerUpdates` provider
 * (account-v1/src/core/signalr/emailBannerUpdates.js). The email-banner SignalR client that
 * the confirmation-email-image styling editor connects to so a background image-generation
 * completion can flip the preview's `isImageProcessed`/`imageUUID` live.
 *
 * Verbatim port EXCEPT:
 * - `$rootScope.$broadcast('accountapp.email-banner-updates.completed', signal)` → the RxJS
 *   `completed$` Observable (the sole subscriber is FtTouchpointStylingComponent → updateBannerImageStatus).
 * - the provider-config URL (`setSignalrUrl('/signalr/email-banner-updates')`, angularjs.module.ts:241-242,
 *   server-mapped in AccountApp Program.cs) → the hardcoded literal below (it is a fixed constant).
 * - `NgZone.run` around `completedSubject.next` — the ONE behavioural addition: v1 got change-detection
 *   for free because the broadcast ran inside the AngularJS digest; the @microsoft/signalr `on` callback
 *   fires OUTSIDE Angular's zone, so without `zone.run` the gallery's `[hidden]`/image src would not repaint
 *   until an unrelated CD tick. (Gate-blind: there is no live SignalR at capture.)
 *
 * The Connected/Connecting double-connect guard, the fire-and-forget `start()` (no `.catch`), and
 * `stopReceiving()` NOT nulling the connection are all kept verbatim for parity.
 *
 * `providedIn:'root'` (singleton, like the v1 provider). Lifecycle ownership for B3 is the styling
 * component's ngOnDestroy calling `stopReceiving()` — exactly mirroring v1's
 * `emailImagesPreview.component.js` `$onDestroy`.
 */
export interface EmailBannerSignal {
  imageType: 'confirmation' | 'prearrival' | 'poststay';
  /** The server emits `contestUuid` (Queue processors / LayersApiController); the consumer's guard
   *  reads `signal.campaignUuid` (a pre-existing v1 mismatch → the auto-update guard never matches a
   *  real signal). Both are typed here; the bug is preserved in updateBannerImageStatus, not here. */
  contestUuid?: string;
  campaignUuid?: string;
  imageUuid: string;
  languageCode: string;
}

@Injectable({ providedIn: 'root' })
export class EmailBannerUpdatesService implements OnDestroy {
  private readonly url = '/signalr/email-banner-updates';
  private connection?: signalR.HubConnection;
  private readonly completedSubject = new Subject<EmailBannerSignal>();
  /** Replaces v1 `$rootScope.$broadcast('accountapp.email-banner-updates.completed', signal)`. */
  readonly completed$: Observable<EmailBannerSignal> = this.completedSubject.asObservable();

  private readonly zone = inject(NgZone);

  /** v1 `startReceiving` (emailBannerUpdates.js:28-34) — Connected/Connecting double-connect guard. */
  startReceiving(): void {
    if (
      !this.connection ||
      (this.connection.state !== signalR.HubConnectionState.Connected &&
        this.connection.state !== signalR.HubConnectionState.Connecting)
    ) {
      this.connection = new signalR.HubConnectionBuilder().withUrl(this.url).build();
      this.connection.on('completed', (signal: EmailBannerSignal) =>
        this.zone.run(() => this.completedSubject.next(signal)),
      );
      this.connection.start();
    }
  }

  /** v1 `stopReceiving` (emailBannerUpdates.js:36-40) — does NOT null-out `this.connection`. */
  stopReceiving(): void {
    if (this.connection) {
      this.connection.stop();
    }
  }

  ngOnDestroy(): void {
    this.stopReceiving();
  }
}
